"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { useToasts } from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import {
  isItemTradable,
  isItemTradableFragment,
} from "../../../../../../apps/web/components/items/validation/tradable"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useSyncFormFields } from "../../../hooks/useSyncFormField"
import { offerFlowSchema } from "../../../lib/zod/schema"
import { useAddress } from "../../../polychain/hooks/useAddress"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { CreateOfferFlowState } from "../../flow-state"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import {
  useLiveUpdateItemsBestOrders,
  useLiveUpdateItemsBestOrdersFragment,
} from "../../hooks/useLiveUpdateItemsBestOrders"
import { CreateOfferToast } from "../../ToastHandler/CreateOfferToast/CreateOfferToast"
import {
  DEFAULT_EXPIRATION,
  OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
  OFFER_EXPIRATION_LOCAL_STORAGE,
} from "../constants"
import { CreateOfferFlowReview } from "./components/CreateOfferFlowReview"
import { CreateOfferFlowReview_itemFragment } from "./components/CreateOfferFlowReview/CreateOfferFlowReview.fragment"
import {
  CreateOfferForm,
  CreateOfferFormSkeleton,
} from "./components/CreateOfferForm"
import { CreateOfferForm_itemFragment } from "./components/CreateOfferForm/CreateOfferForm.fragment"
import { CreateOfferPriceWarningModal } from "./components/CreateOfferPriceWarningModal"
import { CreateOfferPriceWarningModal_itemFragment } from "./components/CreateOfferPriceWarningModal/CreateOfferPriceWarningModal.fragment"
import type { CreateOfferFlowFormValues } from "./types"

const CreateOfferFlowBaseFragment = graphql(
  `
    fragment CreateOfferFlowBase on Item {
      id
      tokenId
      contractAddress
      chain {
        identifier
        wrappedNativeCurrency {
          address
        }
        offerCurrency {
          symbol
          address
        }
        nativeCurrency {
          symbol
        }
      }
      imageUrl
      collection {
        id
        fees {
          totalCreatorFee {
            isRequired
            feeBasisPoints
          }
          openseaFee {
            feeBasisPoints
          }
        }
        floorPrice {
          id
          pricePerItem {
            token {
              unit
            }
          }
        }
      }
      bestOffer {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      ...CreateOfferForm_item
      ...CreateOfferFlowReview_item
      ...useLiveUpdateItemsBestOrders
      ...CreateOfferPriceWarningModal_item
      ...isItemTradable
    }
  `,
  [
    CreateOfferForm_itemFragment,
    CreateOfferFlowReview_itemFragment,
    useLiveUpdateItemsBestOrdersFragment,
    CreateOfferPriceWarningModal_itemFragment,
    isItemTradableFragment,
  ],
)

const CreateOfferFlowQuery = graphql(
  `
    query CreateOfferFlowQuery(
      $identifiers: [ItemIdentifierInput!]!
    ) {
      itemsByIdentifiers(identifiers: $identifiers) {
        id
        tokenId
        contractAddress
        chain {
          identifier
        }
        ...CreateOfferFlowBase
        ...isItemTradable
      }
    }
  `,
  [CreateOfferFlowBaseFragment, isItemTradableFragment],
)

type Props = Pick<CreateOfferFlowState, "items">
type WithQuantity<T> = T & { quantity: number }

export function CreateOfferFlow({ items: itemsInput }: Props) {
  const _address = useAddress()
  const [{ data, fetching }] = useQuery({
    query: CreateOfferFlowQuery,
    variables: {
      identifiers: itemsInput.map(item => ({
        tokenId: item.tokenId,
        contractAddress: item.contractAddress,
        chain: item.chain.identifier,
      })),
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  useEndFlowOnAccountChange()

  const anyItemDisabled = data?.itemsByIdentifiers.some(
    item => !isItemTradable(item),
  )
  if (anyItemDisabled) {
    throw new Error("One or more items disabled for trading.")
  }
  const items = data?.itemsByIdentifiers.flatMap(item => {
    const quantity = itemsInput.find(
      itemInput =>
        itemInput.tokenId === item?.tokenId &&
        itemInput.chain.identifier === item.chain.identifier &&
        itemInput.contractAddress === item.contractAddress,
    )?.quantity
    return item && quantity ? [{ ...item, quantity }] : []
  })

  if (fetching) {
    return <CreateOfferFormSkeleton count={itemsInput.length} />
  }

  if (!items) {
    throw new Error("There was a problem with creating offers.")
  }

  return <CreateOfferFlowBase items={items} />
}

function CreateOfferFlowBase({
  items: itemKeys,
}: {
  items: WithQuantity<FragmentOf<typeof CreateOfferFlowBaseFragment>>[]
}) {
  const { addCustomToast } = useToasts()

  const [view, setView] = useState<"create" | "review" | "success" | "error">(
    "create",
  )

  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false)

  const items = useLiveUpdateItemsBestOrders(
    itemKeys.map(item => ({
      ...readFragment(CreateOfferFlowBaseFragment, item),
      quantity: item.quantity,
    })),
  )

  const methods = useForm({
    defaultValues: useDefaultFormValues(items),
    mode: "onChange",
    resolver: zodResolver(offerFlowSchema),
  })
  const offers = useWatch({ control: methods.control, name: "offers" })
  const offerItems = useMemo(
    () => items.filter(item => offers.find(offer => offer.id === item.id)),
    [items, offers],
  )
  const shouldConfirmPrice = offers.some(offer => offer.shouldConfirmPrice)

  useSyncFormFields({
    fields: items.flatMap((item, index) => [
      {
        name: `offers.${index}.floorPrice` as const,
        value: item.collection?.floorPrice?.pricePerItem.token.unit,
      },
      {
        name: `offers.${index}.bestOffer` as const,
        value: item.bestOffer?.pricePerItem.token.unit,
      },
    ]),
    setValue: methods.setValue,
  })

  const handleFormSubmit = () => {
    if (shouldConfirmPrice) {
      setIsPriceWarningModalOpen(true)
    } else {
      setView("review")
    }
  }

  const showToast = useCallback(
    (success: boolean) => {
      // Show toast when action completes
      const { receiptStatus } = FlowState

      addCustomToast(
        () => (
          <CreateOfferToast
            imageUrls={offerItems.map(item => item.imageUrl || "")}
            price={{
              unit: offers[0]?.price || 0,
              symbol: offers[0]?.offerSymbol || "",
            }}
            status={receiptStatus ?? "FAILED"}
            success={success}
          />
        ),
        {
          id: "create-offer-toast",
          duration: 15_000,
        },
      )
    },
    [offerItems, offers, addCustomToast],
  )

  // For single item flows, use the first item for the price warning modal
  const firstItem = items[0]

  return (
    <FormProvider {...methods}>
      {view === "create" && (
        <CreateOfferForm items={items} onSubmit={handleFormSubmit} />
      )}
      {view === "review" && (
        <CreateOfferFlowReview
          items={items}
          onBack={() => setView("create")}
          onError={() => {
            FlowActions.setReceiptStatus("FAILED")
            showToast(false)
            FlowActions.endFlow()
          }}
          onSuccess={() => {
            FlowActions.setReceiptStatus("SUCCESS")
            FlowActions.onSuccess()
            showToast(true)
            FlowActions.endFlow()
          }}
        />
      )}
      <CreateOfferPriceWarningModal
        item={firstItem}
        onNext={() => setView("review")}
        open={isPriceWarningModalOpen}
        setIsOpen={setIsPriceWarningModalOpen}
      />
    </FormProvider>
  )
}

const useDefaultFormValues = (
  items: WithQuantity<ResultOf<typeof CreateOfferFlowBaseFragment>>[],
): CreateOfferFlowFormValues => {
  const [localStorageOfferExpirationMinutes] = useLocalStorage(
    OFFER_EXPIRATION_LOCAL_STORAGE,
    DEFAULT_EXPIRATION,
  )

  const [localStorageOfferExpirationCustom] = useLocalStorage<
    string | undefined
  >(OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined)

  return {
    expiration: {
      expirationType: localStorageOfferExpirationMinutes,
      customExpiration: localStorageOfferExpirationCustom
        ? new Date(localStorageOfferExpirationCustom)
        : undefined,
    },
    offers: items.map(item => ({
      id: item.id,
      quantity: item.quantity,
      applyCreatorFee: Boolean(
        item.collection?.fees.totalCreatorFee.isRequired,
      ),
      isCreatorFeeRequired: Boolean(
        item.collection?.fees.totalCreatorFee.isRequired,
      ),
      creatorFeeBasisPoints:
        item.collection?.fees.totalCreatorFee.feeBasisPoints || 0,
      openSeaFeeBasisPoints:
        item.collection?.fees.openseaFee?.feeBasisPoints || 0,
      price: Number.NaN,
      floorPrice: item.collection?.floorPrice?.pricePerItem.token.unit,
      bestOffer: item.bestOffer?.pricePerItem.token.unit,
      offerSymbol: item.chain.offerCurrency.symbol,
      nativeCurrencySymbol: item.chain.nativeCurrency.symbol,
      isWrappedNativeCurrency:
        item.chain.offerCurrency.address ===
        item.chain.wrappedNativeCurrency?.address,
    })),
  }
}
